import { useState } from "react";
import { Knopf } from "../design/Kachel";
import { Dialog } from "../design/Dialog";
import { Auswahl, Textfeld } from "../design/Formular";
import { Abweisung, Status } from "../design/Hinweis";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";

/** Feste Wortlaute (oberflaeche.md §9): Bitte nach dem ersten Passkey und Hinweis nach dem Versand des Codes (V-21). */
export const OHNE_KONTAKT = "Ohne Wiederherstellungskontakt ist bei Verlust des Geräts in jedem Revier eine neue Einladung nötig.";
export const codeGesendet = (adresse: string) =>
  `Der Code wurde an ${adresse} gesendet. Kommt er nicht an, geht es mit „Später“ weiter; der Kontakt lässt sich jederzeit unter „Konto“ neu hinterlegen.`;

/** Titel mit weichem Trennstrich an der Wortfuge: bricht nur in schmalen Ansichten um (design/README K96). */
const TITEL = "Wiederherstellungs\u00ADkontakt";

/**
 * Wiederherstellungskontakt hinterlegen (oberflaeche.md §7): „Art“, „Adresse“, „Hinterlegen“, „Später“; danach
 * „Einmalcode“, „Bestätigen“, „Später“. Nach dem Versand nennt ein Hinweis die Adresse und den Weg ohne Code (V-21).
 */
function KontaktFormular({ onFertig, onSpaeter }: { onFertig: () => void; onSpaeter: () => void }) {
  const [art, setArt] = useState<string | null>("Telefonnummer");
  const [adresse, setAdresse] = useState("");
  const [code, setCode] = useState("");
  const [schritt, setSchritt] = useState<"adresse" | "code">("adresse");
  const [grund, setGrund] = useState<string | null>(null);

  async function hinterlegen() {
    setGrund(null);
    const a = await befehl("WiederherstellungskontaktHinterlegen", { Art: art, Adresse: adresse });
    if (a.ok) setSchritt("code");
    else setGrund(abweisungsgrund(a));
  }

  async function bestaetigen() {
    setGrund(null);
    const a = await befehl("WiederherstellungskontaktBestaetigen", { Adresse: adresse, Einmalcode: code });
    if (a.ok) onFertig();
    else setGrund(abweisungsgrund(a));
  }

  return schritt === "adresse" ? (
    <div className="formular">
      <p className="erkl">Bei Verlust des Geräts kommt das Konto über diesen Kontakt zurück.</p>
      <Auswahl label="Art" optionen={["Telefonnummer", "E-Mail-Adresse"]} wert={art} onChange={setArt} />
      <Textfeld label="Adresse" wert={adresse} onChange={setAdresse} typ={art === "E-Mail-Adresse" ? "email" : "tel"} />
      <Abweisung grund={grund} />
      <div className="knopfreihe">
        <Knopf onPress={hinterlegen}>Hinterlegen</Knopf>
        <Knopf art="zweit" onPress={onSpaeter}>Später</Knopf>
      </div>
    </div>
  ) : (
    <div className="formular">
      <Status>{codeGesendet(adresse.trim())}</Status>
      <Textfeld label="Einmalcode" wert={code} onChange={setCode} eingabemodus="numeric" autoComplete="one-time-code" mono />
      <Abweisung grund={grund} />
      <div className="knopfreihe">
        <Knopf onPress={bestaetigen}>Bestätigen</Knopf>
        <Knopf art="zweit" onPress={onSpaeter}>Später</Knopf>
      </div>
    </div>
  );
}

/** `dialog` „Wiederherstellungskontakt“ (Kontoansicht „Hinterlegen“, „Hinterlegen“ der Bitte). „Später“ schließt ihn. */
export function KontaktDialog({ offen, onSchliessen, onFertig, onSpaeter }: {
  offen: boolean; onSchliessen: () => void; onFertig?: () => void; onSpaeter?: () => void;
}) {
  return (
    <Dialog name="Wiederherstellungskontakt" titel={TITEL} symbol="telefon" offen={offen} onSchliessen={onSchliessen}>
      <KontaktFormular onFertig={onFertig ?? onSchliessen} onSpaeter={onSpaeter ?? onSchliessen} />
    </Dialog>
  );
}

const beantwortetSchluessel = (uid: string) => `dj-kontaktbitte-beantwortet-${uid}`;

function istBeantwortet(uid: string | null): boolean {
  try { return !!uid && localStorage.getItem(beantwortetSchluessel(uid)) === "1"; } catch { return false; }
}

/**
 * Gestalt der Bitte (design/README §9.12, Bericht B3 F1): ein nicht modaler `dialog` „Wiederherstellungskontakt“ als
 * erster Eintrag der Liste in Meine Drückjagden, nur Titel, Hinweis (§9) und die beiden Handlungen nebeneinander. So
 * bleiben Kopfnavigation und Liste unverändert, und „Hinterlegen“ und „Später“ stehen auch auf kleinen Telefonen im
 * Bild. „Hinterlegen“ öffnet das Formular als eigenen Dialog. Der Titel bricht in schmalen Ansichten nur an der Wortfuge
 * um (weicher Trennstrich, design/README K96); der Name des Dialogs bleibt der Begriff ohne Trennstrich.
 */
export function KontaktBitteKarte({ onHinterlegen, onSpaeter }: { onHinterlegen: () => void; onSpaeter: () => void }) {
  return (
    <section className="text-bereich platte kontaktbitte" role="dialog" aria-modal="false" aria-label="Wiederherstellungskontakt">
      <h2 className="kontaktbitte__t"><Symbol name="telefon" /><span>{TITEL}</span></h2>
      <Status>{OHNE_KONTAKT}</Status>
      <div className="raster raster--2 kontaktbitte__knoepfe">
        <Knopf onPress={onHinterlegen}>Hinterlegen</Knopf>
        <Knopf art="zweit" onPress={onSpaeter}>Später</Knopf>
      </div>
    </section>
  );
}

/**
 * Bitte um einen Wiederherstellungskontakt (Domänenmodell §3, F-047a, F-04C; zugang/06): solange das Konto keinen
 * Kontakt hat, bittet die App einmal darum und erklärt die Folgen (oberflaeche.md §9). „Später“ (in der Bitte oder im
 * Formular) beantwortet die Bitte auf diesem Gerät endgültig; danach bleibt nur der Hinweis in der Kontoansicht. Die Bitte
 * ist nicht modal, damit die App daneben bedienbar bleibt; die Ansicht stellt sie in ihre Liste (`MeineDrueckjagden`).
 */
export function KontaktBitte() {
  const { uid, art } = useSitzung();
  const konto = useDokument(uid && art === "passkey" ? `personen/${uid}/konto/aktuell` : null);
  const [beantwortet, setBeantwortet] = useState<string | null>(null);
  const [formular, setFormular] = useState(false);
  const kontakte = (konto.daten?.["Wiederherstellungskontakte"] as unknown[] | undefined) ?? [];
  if (!uid || art !== "passkey" || !konto.daten || kontakte.length > 0 || beantwortet === uid || istBeantwortet(uid)) return null;

  function spaeter() {
    try { localStorage.setItem(beantwortetSchluessel(uid!), "1"); } catch { /* ohne Speicher fragt die App beim nächsten Mal wieder */ }
    setFormular(false);
    setBeantwortet(uid);
  }

  return (
    <>
      {formular ? null : <KontaktBitteKarte onHinterlegen={() => setFormular(true)} onSpaeter={spaeter} />}
      <KontaktDialog offen={formular} onSchliessen={() => setFormular(false)} onSpaeter={spaeter}
        onFertig={() => { setFormular(false); setBeantwortet(uid); }} />
    </>
  );
}
